/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { FC, useEffect, useState } from "react";
import PatientHubForm from "components/forms/PatientHubForm";
import { Box, Grid } from "@mui/material";
import HeaderPersonInformation from "components/views/HeaderPersonInformation";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import {
  AssetInterface,
  HeaderEnum,
  PatientAssetRelationInterface,
  PatientInterface,
} from "medicalsystem-shared";
import DocumentViewer from "components/views/DocumentViewer";
import { DocumentFolder } from "interfaces";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";

/**
 * Props for the PatientHubDataContent component.
 * @interface NewVitalsDataContentProps
 * @property {string} [patientId] - The ID of the patient to display information for
 */
interface NewVitalsDataContentProps {
  patientId?: string;
}

/**
 * Content component for the patient hub interface.
 * Provides a centralized view of patient information including demographics,
 * medical history, documents, and related data. Acts as a dashboard for patient management.
 * @param {NewVitalsDataContentProps} props - Component props
 * @returns {React.ReactElement} The patient hub content component
 */
const PatientHubDataContent: FC<NewVitalsDataContentProps> = ({ patientId }) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [patientModel, setPatientModel] = useState<PatientInterface>();
  const [documents, setDocuments] = useState<DocumentFolder[]>([]);
  const [isDataUpdated, setIsDataUpdated] = useState(false);

  useEffect(() => {
    if (patientId) {
      sendRequest(CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS, onGetPatient, "GET", undefined, [
        {
          header: HeaderEnum.PATIENT_ID,
          value: patientId,
        },
      ]);
      getAssets(patientId);
    }
  }, [patientId]);

  useEffect(() => {
    if (isDataUpdated) {
      getAssets(patientId);
      setIsDataUpdated(false);
    }
  }, [isDataUpdated]);

  const onGetPatient = (patient: PatientInterface) => {
    setPatientModel(patient);
  };

  const getAssets = (patientId: string) => {
    sendRequest<PatientAssetRelationInterface[]>(
      CoreAPI_URLs.ASSET_PATIENT_RELATION,
      (response) => {
        if (response) {
          const updatedDocuments = response.map((item) => ({
            folderName: item.customCategory || item.asset.category,
            documents: [item.asset as AssetInterface],
          }));
          setDocuments(updatedDocuments);
        }
      },
      "GET",
      undefined,
      [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
    );
  };

  return (
    <Box>
      {patientModel && (
        <Grid container spacing={4}>
          <Grid item xs={12}>
            {patientModel.person && (
              <HeaderPersonInformation personInformation={patientModel.person} />
            )}
          </Grid>
          <Grid item xs={12}>
            <PatientHubForm patientModel={patientModel} />
          </Grid>
          <Grid item xs={12}>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW_DOCUMENTS]}
            >
              <DocumentViewer
                folders={documents}
                patientId={patientId}
                setIsDataUpdated={setIsDataUpdated}
              />
            </PermissionWrapper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PatientHubDataContent;
